export default function RQSABudget(propertyData, userPersonalInfo, geoData) {

    // Pull the key information from the userPersonalInfo object
    const userFinancesHouseholdIncome = userPersonalInfo.householdIncome;
    const userFinancesAvailableFunds = userPersonalInfo.availableFunds;
    const userFinancesRelationToProperty = userPersonalInfo.relationToProperty;

    // Pull the key information from the propertyData object
    const financialData = propertyData?.data?.scenario_modeling?.individual_breakdowns?.[0]?.financials_no_improvements_made;

    // Pull the key information from the geoData object
    const fipsData = geoData.fips
    const zipData = geoData.zip

    // Pull the key information from the financialData object
    const financialDataBreakdown = financialData?.breakdown;
    const budgeting10YearBreakdown = financialDataBreakdown?.budgeting;

    // Take the 10 year breakdown array and get just the values
    const budgeting10YearBreakdownAggregated = budgeting10YearBreakdown.map(item => item.value);
    // console.log('Budgeting 10 Year Breakdown Aggregated', budgeting10YearBreakdownAggregated);

    // Set the growth rate for available funds 
    const availableFundsGrowthRateNone = 0.00;
    const availableFundsGrowthRateLow = 0.025;

    // Take the current available funds and model it out over next 10 years using the growth rate
    const availableFunds10YearProjection = Array(10).fill(0).map((_, index) => {
        // Calculate compound growth: initial * (1 + rate)^year
        return Math.round(userFinancesAvailableFunds * Math.pow(1 + availableFundsGrowthRateNone, index + 1));
    });
    // console.log('Available Funds 10 Year Projection', availableFunds10YearProjection);

    // Take the budgeting10YearBreakdownAggregated and availableFunds10YearProjection arrays and calculate two new arrays
    // First array: The difference between the available funds and the budgeting10YearBreakdownAggregated in raw numbers
    const availableFundsDifference = availableFunds10YearProjection.map((fund, index) => fund - budgeting10YearBreakdownAggregated[index]);
    // console.log('Available Funds Difference', availableFundsDifference);

    // Second array: The difference between the available funds and the budgeting10YearBreakdownAggregated as a percentage of the available funds (compare again 1 ie .25 vs -.25)
    const availableFundsDifferencePercentage = availableFundsDifference.map((diff, index) => diff / budgeting10YearBreakdownAggregated[index]);
    // console.log('Available Funds Difference Percentage', availableFundsDifferencePercentage);

    // Get the min, max, mean, median, average, and growth rate of the availableFundsDifferencePercentage array
    const availableFundsDifferencePercentageMin = Math.min(...availableFundsDifferencePercentage);
    const availableFundsDifferencePercentageMax = Math.max(...availableFundsDifferencePercentage);
    const availableFundsDifferencePercentageMean = availableFundsDifferencePercentage.reduce((acc, curr) => acc + curr, 0) / availableFundsDifferencePercentage.length;
    const availableFundsDifferencePercentageMedian = availableFundsDifferencePercentage.sort((a, b) => a - b)[Math.floor(availableFundsDifferencePercentage.length / 2)];
    const availableFundsDifferencePercentageAverage = availableFundsDifferencePercentage.reduce((acc, curr) => acc + curr, 0) / availableFundsDifferencePercentage.length;
    const availableFundsDifferencePercentageGrowthRate = Math.pow(
        availableFundsDifferencePercentage[availableFundsDifferencePercentage.length - 1] /
        availableFundsDifferencePercentage[0],
        1 / (availableFundsDifferencePercentage.length - 1)
    ) - 1;

    // Get the index of the min, max, mean, median, average, and growth rate of the availableFundsDifferencePercentage array
    const availableFundsDifferencePercentageMinIndex = availableFundsDifferencePercentage.indexOf(availableFundsDifferencePercentageMin);
    const availableFundsDifferencePercentageMaxIndex = availableFundsDifferencePercentage.indexOf(availableFundsDifferencePercentageMax);
    const availableFundsDifferencePercentageMeanIndex = availableFundsDifferencePercentage.indexOf(availableFundsDifferencePercentageMean);
    const availableFundsDifferencePercentageMedianIndex = availableFundsDifferencePercentage.indexOf(availableFundsDifferencePercentageMedian);
    const availableFundsDifferencePercentageAverageIndex = availableFundsDifferencePercentage.indexOf(availableFundsDifferencePercentageAverage);

    
    
    let budgetRiskLevel = 1;
    if (availableFundsDifferencePercentageMin > 3) {
        budgetRiskLevel = 1;
    } else if (availableFundsDifferencePercentageMin > 2 && availableFundsDifferencePercentageMin <= 3) {
        budgetRiskLevel = 2;
    } else if (availableFundsDifferencePercentageMin > 1 && availableFundsDifferencePercentageMin <= 2) {
        budgetRiskLevel = 3;
    } else if (availableFundsDifferencePercentageMin > 0.5 && availableFundsDifferencePercentageMin <= 1) {
        budgetRiskLevel = 4;
    } else if (availableFundsDifferencePercentageMin > 0 && availableFundsDifferencePercentageMin <= 0.5) {
        budgetRiskLevel = 5;
    } else if (availableFundsDifferencePercentageMin > -0.5 && availableFundsDifferencePercentageMin <= 0) {
        budgetRiskLevel = 6;
    } else if (availableFundsDifferencePercentageMin > -1 && availableFundsDifferencePercentageMin <= -0.5) {
        budgetRiskLevel = 7;
    } else if (availableFundsDifferencePercentageMin > -2 && availableFundsDifferencePercentageMin <= -1) {
        budgetRiskLevel = 8;
    } else if (availableFundsDifferencePercentageMin <= -2) {
        budgetRiskLevel = 9;
    }

    if (availableFundsDifferencePercentageGrowthRate > 0) {
        budgetRiskLevel -= 1;
    } else if (availableFundsDifferencePercentageGrowthRate < 0) {
        budgetRiskLevel += 1;
    }
    
    if (budgetRiskLevel < 1) {
        budgetRiskLevel = 1;
    } else if (budgetRiskLevel > 9) {
        budgetRiskLevel = 9;
    }
    
    const budgetOutputObject = {
        min: availableFundsDifferencePercentageMin,
        minIndex: availableFundsDifferencePercentageMinIndex,
        max: availableFundsDifferencePercentageMax,
        maxIndex: availableFundsDifferencePercentageMaxIndex,
        mean: availableFundsDifferencePercentageMean,
        meanIndex: availableFundsDifferencePercentageMeanIndex,
        median: availableFundsDifferencePercentageMedian,
        medianIndex: availableFundsDifferencePercentageMedianIndex,
        average: availableFundsDifferencePercentageAverage,
        growthRate: availableFundsDifferencePercentageGrowthRate,
        budgetRiskLevel: budgetRiskLevel
    }
    // console.log('Budget Output Object', budgetOutputObject);

    return budgetOutputObject;

}